import "core-js/modules/es.array.sort";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.replace";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.array.sort";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.replace";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form.
 *
 * @namespace productForm
 */

import { formatMoney } from '@shopify/theme-currency';
import axios from 'axios';
import cssClasses from '~/helpers/cssClasses';
import { on } from '~/helpers/utils';
import bind from '~/mixins/bind';
import ProductSwatches from '~/components/product-swatches';
import QuantitySelector from '~/components/quantity-selector';
/**
 * DOM selectors.
 */

const selectors = {
  addToCart: '[js-product-form="addToCart"]',
  addToCartText: '[js-product-form="addToCartText"]',
  comparePrice: '[js-product-form="comparePrice"]',
  container: '[js-product-form="container"]',
  defaultSelectOption: '[js-product-form="defaultSelectOption"]',
  formOptionColor: '[js-product-form="product-form__option-color"]',
  descriptionSection: '[js-template-product="description"]',
  detailAnchor: '[js-product-form="detailAnchor"]',
  masterSelector: '[js-product-form="masterSelector"]',
  maxQuantityInput: '[js-product-form="maxQuantityInput"]',
  notification: '[js-product-form="notification"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  productJson: '[js-product-json]',
  quantitySelector: '[js-quantity-selector="container"]',
  sizeOption: '[js-product-form="product-form__option-size"]',
  sizeSelector: '[js-product-form-default="sizeSelector"]',
  swatchListContainer: '[js-product-form="swatchListContainer"]'
};
/**
 * Create a new product form object.
 * @param {Object} config - Configuration.
 */

export default (config => {
  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false
  };
  /**
   * DOM node selectors.
   */

  const container = document.querySelector("[js-product-form=\"".concat(config.container, "\"]"));
  const nodeSelectors = {
    addToCart: container.querySelector(selectors.addToCart),
    comparePrice: container.querySelector(selectors.comparePrice),
    descriptionSection: document.querySelector(selectors.descriptionSection),
    detailAnchor: container.querySelector(selectors.detailAnchor),
    formOptionColor: container.querySelector(selectors.formOptionColor),
    masterSelector: container.querySelector(selectors.masterSelector),
    maxQuantityInput: container.querySelector(selectors.maxQuantityInput),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    productJson: container.querySelector(selectors.productJson),
    quantitySelector: container.querySelector(selectors.quantitySelector),
    sizeOption: document.querySelector(selectors.sizeOption),
    sizeSelector: document.querySelector(selectors.sizeSelector),
    swatchListContainer: container.querySelector(selectors.swatchListContainer)
  };
  /**
   * Initialise component bind.
   */

  const binder = bind(container);
  /**
   * Global instances.
   */

  const productData = Object.freeze(JSON.parse(nodeSelectors.productJson.innerHTML));
  const variantData = productData.variants;
  let quantitySelector = {};
  let currentVariant = {};
  /**
   * Initialise component.
   */

  function init() {
    if (binder.isSet()) {
      return;
    }

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
    }

    setDefaultState();
    setEventListeners();
    setEventBusListeners();
    initDefaultSizeSelectorState();
    initSwatches();
    binder.set();
    salePriceOnLoad();
  }
  /**
   * Deselect default size selection option
   */


  function initDefaultSizeSelectorState() {
    if (!nodeSelectors.sizeSelector) {
      return;
    }

    const option = document.createElement('option');
    option.text = theme.strings.selectASize;
    option.value = theme.strings.selectASize;
    option.setAttribute('selected', true);
    option.setAttribute('js-product-form', 'defaultSelectOption');
    nodeSelectors.sizeSelector.prepend(option);
    nodeSelectors.sizeSelector.classList.add(cssClasses.default);
    disableBuyButton();
  }
  /**
   * Disables buy button on page load
   */


  function disableBuyButton() {
    const addToCartButton = nodeSelectors.addToCart;
    addToCartButton.classList.add(cssClasses.disabled);
    addToCartButton.parentNode.classList.add(cssClasses.disabled);
    addToCartButton.querySelector(selectors.addToCartText).innerText = theme.strings.selectASize;
  }
  /**
   * Enables buy button after shoe size selection
   */


  function enableBuyButton() {
    const addToCartButton = nodeSelectors.addToCart;

    if (!addToCartButton.classList.contains(cssClasses.disabled)) {
      return;
    }

    addToCartButton.classList.remove(cssClasses.disabled);
    addToCartButton.parentNode.classList.remove(cssClasses.disabled);
  }
  /**
   * Removes default option after shoe size selection
   */


  function removeDefaultOption() {
    const defaultOption = document.querySelector(selectors.defaultSelectOption);

    if (!defaultOption) {
      return;
    }

    nodeSelectors.sizeSelector.removeChild(defaultOption);
    nodeSelectors.sizeSelector.classList.remove(cssClasses.default);
  }
  /**
   * Set default state.
   */


  function setDefaultState() {
    if (theme.features.productSwatches) {
      ProductSwatches({
        data: productData
      }).init();
    }

    setQuantitySelectors();
    updateFormState();
  }
  /**
   * Update notification state.
   * @param {Object} variant - Current variant object.
   */


  function updateNotificationState(variant) {
    if (isNotificationCreated()) {
      container.querySelector(selectors.notification).remove();
    }

    if (variant) {
      const quantity = getVariantInventoryQuantity(variant);

      if (!quantity) {
        return;
      }

      if (quantity <= theme.features.inventoryThreshold && quantity > 0) {
        container.appendChild(createNotification(quantity));
      }
    }
  }
  /**
   * Check if notification exists.
   * @returns {Boolean}
   */


  function isNotificationCreated() {
    return Boolean(container.querySelector(selectors.notification));
  }
  /**
   * Create notification node.
   * @param {Number} quantity - Current variant inventory quantity.
   * @returns {HTMLElement}
   */


  function createNotification(quantity) {
    const message = getLowStockMessage(quantity);
    const notification = document.createElement('div');
    notification.classList.add('notification');
    notification.innerHTML = getNotificationTemplate(message);
    notification.setAttribute('js-product-form', 'notification');
    return notification;
  }
  /**
   * Get notification template.
   * @param {String} message - text to render inside notification.
   * @returns {String}
   */


  function getNotificationTemplate(message) {
    return "\n      <div class=\"notification__text-wrapper\">\n        <p class=\"notification__text\">\n          ".concat(message, "\n        </p>\n      </div>\n    ");
  }
  /**
   * Get low stock notification message correctly pluralised.
   * @param {Number} quantity - Quantity of line item.
   * @returns {String}
   */


  function getLowStockMessage(quantity) {
    if (quantity === 1) {
      return theme.low_stock_message.single;
    }

    return theme.low_stock_message.multiple.replace('%quantity%', quantity);
  }
  /**
   * Get variant inventory quantity from product-json.liquid variant data.
   * @param {Object || undefined} - Variant object.
   */


  function getVariantInventoryQuantity(variant) {
    if (!variantData.length) {
      return 0;
    }

    return Object.keys(variantData).map(key => {
      if (variantData[key].id === variant.id) {
        return variantData[key].inventory_quantity;
      }

      return null;
    }).filter(Boolean)[0];
  }
  /**
   * Set EventBus listeners.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('Variant:priceChange', response => onPriceChange(response));
    Frame.EventBus.listen('ProductSwatches:available', response => handleSwatchAvailability(response));
    Frame.EventBus.listen('ProductSwatches:optionSelected', response => handleOptionChange(response));
    Frame.EventBus.listen('ProductSwatches:reset', () => resetForm());
    Frame.EventBus.listen('ProductSwatches:soldOut', () => updateAddToCartState(theme.strings.soldOut));
  }
  /**
   * Set event listeners on select elements.
   */


  function setEventListeners() {
    [...nodeSelectors.optionSelector].forEach(element => {
      if (element.nodeName === 'SELECT') {
        on('change', element, () => handleSelectChange());
      }
    });

    if (nodeSelectors.detailAnchor) {
      on('click', nodeSelectors.detailAnchor, event => scrollToDescription(event));
    }
  }
  /**
   * scroll to detail section on click
   */


  function scrollToDescription(event) {
    event.preventDefault();
    nodeSelectors.descriptionSection.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }
  /**
   * Get currently selected options from product form.
   * - Works with all form elements with an option selector selector.
   * @returns {Array}
   */


  function getCurrentOptions() {
    const currentOptions = [];
    [...nodeSelectors.optionSelector].forEach(element => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;
          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;
        currentOptions.push(currentOption);
      }
    });
    return currentOptions;
  }
  /**
   * Find variant from product.variants based on selected values.
   * @returns {Object|undefined}
   */


  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = productData.variants;
    let found = false;
    variants.forEach(variant => {
      let satisfied = true;
      selectedValues.forEach(option => {
        if (satisfied) {
          satisfied = option.value === variant[option.index];
        }
      });

      if (satisfied) {
        found = variant;
      }
    });
    return found || null;
  }
  /**
   * Event handler for when a variant price changes.
   * @param {Object} response - Selected variant data.
   */


  function onPriceChange(response) {
    const form = document.querySelector("[js-product-form=\"".concat(config.container, "\"]"));
    const priceSelector = form.querySelector(selectors.price);
    priceSelector.setAttribute('data-price', response.price);
    priceSelector.innerText = formatMoney(response.price, theme.moneyFormat);

    if (form.querySelector(selectors.comparePrice)) {
      const comparePriceSelector = form.querySelector(selectors.comparePrice);
      comparePriceSelector.setAttribute('data-price', response.compare_at_price);

      if (response.compare_at_price > 0) {
        comparePriceSelector.innerText = formatMoney(response.compare_at_price, theme.moneyFormat);
        comparePriceSelector.classList.remove(cssClasses.hidden);
        nodeSelectors.price.classList.add(cssClasses.active);
      } else {
        comparePriceSelector.innerText = '';
        comparePriceSelector.classList.add(cssClasses.hidden);
        nodeSelectors.price.classList.remove(cssClasses.active);
      }
    }

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }
  /**
   * Turn sale price red on load.
   */


  function salePriceOnLoad() {
    const form = document.querySelector("[js-product-form=\"".concat(config.container, "\"]"));
    const comparePrice = form.querySelector(selectors.comparePrice);

    if (comparePrice && !comparePrice.classList.contains(cssClasses.hidden)) {
      nodeSelectors.price.classList.add(cssClasses.active);
    }
  }
  /**
   * Handle default availability.
   */


  function handleSwatchAvailability(response) {
    if (!response.formCompleted) {
      disableAddToCart(theme.strings.prompt);
    }
  }
  /**
   * Event handler for when an input changes on the form.
   * @param {Object} response - Pay load from option selected.
   */


  function handleOptionChange(response) {
    Frame.EventBus.emit('ProductForm:change', {
      currentOptions: getCurrentOptions(),
      currentVariant: response.formCompleted ? getVariantFromOptions() : null,
      currentInventory: getVariantInventoryQuantity(getVariantFromOptions())
    });

    if (!response.formCompleted) {
      return;
    }

    updateFormState();
  }
  /**
   * Event handler for when a select element changes on the store.
   */


  function handleSelectChange() {
    enableBuyButton();
    removeDefaultOption();
    updateFormState();
  }
  /**
   * Update form state based on current selections.
   */


  function updateFormState() {
    const variant = getVariantFromOptions();
    updateNotificationState(variant);
    updateAddToCartState(variant);
    updateMaxQuantityState(variant);

    if (!variant) {
      return;
    }

    updateMasterSelect(variant);
    updatePrice(variant);
    currentVariant = variant;

    if (settings.enableHistoryState) {
      updateHistoryState(variant);
    }
  }
  /**
   * Updates the DOM state of the add to cart button.
   * @param {{Object || undefined}} variant - Currently selected variant.
   */


  function updateAddToCartState(variant) {
    if (variant) {
      enableAddToCart();
      nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden);
    } else {
      disableAddToCart();
      nodeSelectors.priceWrapper.classList.add(cssClasses.hidden);
      nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = theme.strings.unavailable;
      return;
    }

    if (variant.available) {
      enableAddToCart();
      nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = theme.strings.addToCart;
    } else {
      disableAddToCart();
      nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = theme.strings.soldOut;
    }
  }
  /**
   * Update max quantity value of quantity selectors.
   * @param {Object} variant - Currently selected variant.
   */


  function updateMaxQuantityState(variant) {
    const quantity = getVariantInventoryQuantity(variant) > 0 ? getVariantInventoryQuantity(variant) : 999;
    nodeSelectors.maxQuantityInput.value = quantity;
    quantitySelector.updateAvailability(variant.available);
    updateOptionText(variant.available);
    quantitySelector.updateMax(quantity);
  }
  /**
   * Update inner text of select option for back in stock messaging.
   * @param {Boolean} availability - Availability of currently selected variant.
   */


  function updateOptionText(variantAvailable) {
    if (!nodeSelectors.sizeOption) {
      return;
    }

    const size = getCurrentOptions()[1].value;
    const options = nodeSelectors.sizeOption.querySelectorAll('option');
    const selectedOption = [...options].filter(option => option.value === size)[0];
    [...options].forEach(option => {
      if (option.value === size && !variantAvailable) {
        option.innerText = "".concat(option.value).concat(theme.low_stock_message.outOfStock);
      } else {
        option.innerText = option.value;
      }
    });

    if (variantAvailable) {
      selectedOption.parentNode.classList.remove('out-of-stock');
      return;
    }

    selectedOption.parentNode.classList.add('out-of-stock');
  }
  /**
   * Trigger event when variant price changes.
   * @param {Object} variant - Currently selected variant.
   */


  function updatePrice(variant) {
    Frame.EventBus.emit('Variant:priceChange', variant);
  }
  /**
   * Update hidden master select of variant change.
   * @param {Object} variant - Currently selected variant.
   */


  function updateMasterSelect(variant) {
    nodeSelectors.masterSelector.value = variant.id;
  }
  /**
   * Update history state for product deep linking.
   * @param {Object} variant - Currently selected variant.
   */


  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = "\n      ".concat(window.location.protocol, "//").concat(window.location.host).concat(window.location.pathname, "?variant=").concat(variant.id, "\n    ");
    window.history.replaceState({
      path: newurl
    }, '', newurl);
  }
  /**
   * Reset form and assign prompt text to add to cart button.
   */


  function resetForm() {
    disableAddToCart();

    if (productData.available) {
      nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = theme.strings.prompt;
    }
  }
  /**
   * Set Quantity Selector component.
   */


  function setQuantitySelectors() {
    quantitySelector = QuantitySelector(nodeSelectors.quantitySelector.id);
    quantitySelector.init();
  }
  /**
   * Add disable state to add to cart button.
   * @param {String} label - Text to use on button.
   */


  function disableAddToCart() {
    let label = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : theme.strings.unavailable;
    nodeSelectors.addToCart.setAttribute('disabled', true);
    nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = label;
  }
  /**
   * Set add to cart button to active.
   */


  function enableAddToCart() {
    nodeSelectors.addToCart.removeAttribute('disabled');
    nodeSelectors.addToCart.querySelector(selectors.addToCartText).innerText = theme.strings.addToCart;
  }
  /**
   * Setting up swatches
   */


  function initSwatches() {
    if (!nodeSelectors.swatchListContainer) {
      return;
    }

    const productMtoString = nodeSelectors.swatchListContainer.getAttribute('data-mto');
    const productStyleNumber = nodeSelectors.swatchListContainer.getAttribute('data-style-number');
    const productSelectedColor = nodeSelectors.swatchListContainer.getAttribute('data-selected-color');
    const requestUrl = "/collections/all/".concat(productStyleNumber, ",").concat(productMtoString, "?view=data");
    axios.get(requestUrl).then(response => {
      nodeSelectors.formOptionColor.classList.remove(cssClasses.hidden);
      GenerateHtmlList(response.data, productSelectedColor);
    }).catch(error => {
      throw new Error('Could not find ', error);
    });
  }
  /**
   * Generate html <li>
   * @param {Object} products - products from the same family.
   * @param {String} productSelectedColor - product's current color.
   */


  function GenerateHtmlList(products, productSelectedColor) {
    const productsByColor = {};
    products.forEach(product => {
      const productColor = product.variants[0].option1;
      const productHandle = product.handle;
      const productSwatchUrl = product.images.filter(imageUrl => {
        return imageUrl.includes('-swatch');
      });
      productsByColor[productColor] = {
        productHandle,
        productSwatchUrl
      };
    });
    const productBySortedColor = sortingObject(productsByColor);
    const htmlListString = createListString(productBySortedColor, productSelectedColor);
    nodeSelectors.swatchListContainer.innerHTML = htmlListString;
  }
  /**
  * Sorting object alphabetically
  * @param {Object} products - products from the same family using color as keys.
  */


  function sortingObject(products) {
    const productBySortedColor = {};
    const productKeyList = Object.keys(products).sort();
    productKeyList.forEach(key => {
      productBySortedColor[key.toLowerCase()] = {
        color: key,
        handle: products[key].productHandle,
        swatchUrl: products[key].productSwatchUrl[0]
      };
    });
    return productBySortedColor;
  }
  /**
   * Generate <li> string
   * @param {Object} sortProducts - product sorted by color.
   * @param {String} selectedColor - product's current color.
   */


  function createListString(sortedProducts, selectedColor) {
    let listString = '';
    const keys = Object.keys(sortedProducts);
    keys.forEach(key => {
      listString += "\n      <li class=\"product-form__swatch-list ".concat(key === selectedColor ? cssClasses.active : '', "\" data-swatch-color=\"").concat(key.toLowerCase(), "\">\n        <a href=\"").concat(theme.shopUrl, "/products/").concat(sortedProducts[key].handle, "\">\n          <img src=\"https:").concat(sortedProducts[key].swatchUrl, "\" alt=\"swatch color ").concat(key.toLowerCase(), "\">\n        </a>\n        <div class=\"product-form__swatch-tooltip\">").concat(key.toUpperCase(), "</div>\n      </li>");
    });
    return listString;
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    getVariantFromOptions
  });
});